import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { BRAND, LEGAL_ARTICLES } from '@va/shared';
import { isDesktop } from '../platform';
import { useAuth } from '../state/auth';
import { useCart } from '../state/cart';
import { BrandLockup } from './Brand';
import { IconCart, IconGift, IconLibrary, IconLifebuoy, IconLogout, IconMenu, IconReceipt, IconSettings, IconShield, IconUser, IconMonitor } from './icons';
import { LicenseBanner } from './LicenseBanner';
import { UpdateBanner } from './UpdateBanner';

function UserMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!user) {
    return (
      <Link to="/login" state={{ from: loc.pathname + loc.search }} className="btn btn-sm btn-outline">
        Sign in
      </Link>
    );
  }
  const initials = (user.displayName || user.email).slice(0, 2).toUpperCase();
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="icon-btn" onClick={() => setOpen((o) => !o)} aria-label="Account menu" aria-expanded={open}>
        <span className="avatar">{initials}</span>
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="menu-head">
            <div style={{ color: 'var(--va-headline)', fontWeight: 600 }}>{user.displayName || 'Your account'}</div>
            <div className="small muted">{user.email}</div>
          </div>
          <Link to="/library" role="menuitem"><IconLibrary size={16} /> My Library</Link>
          <Link to="/account" role="menuitem"><IconUser size={16} /> Account</Link>
          <Link to="/account/orders" role="menuitem"><IconReceipt size={16} /> Orders</Link>
          <Link to="/account/devices" role="menuitem"><IconMonitor size={16} /> Devices</Link>
          <Link to="/redeem" role="menuitem"><IconGift size={16} /> Redeem a code</Link>
          {isDesktop && <Link to="/settings" role="menuitem"><IconSettings size={16} /> App settings</Link>}
          {user.role === 'admin' && !isDesktop && <Link to="/admin" role="menuitem"><IconShield size={16} /> Admin</Link>}
          <button
            role="menuitem"
            onClick={async () => {
              await logout();
              nav('/');
            }}
          >
            <IconLogout size={16} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function Layout() {
  const cart = useCart();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0 });
  }, [loc.pathname]);

  const links = (
    <>
      <NavLink to="/" end className="nav-link">Store</NavLink>
      <NavLink to="/library" className="nav-link">My Library</NavLink>
      <NavLink to="/support" className="nav-link">Support</NavLink>
      {!isDesktop && <NavLink to="/download" className="nav-link">Get the app</NavLink>}
      {isDesktop && <NavLink to="/settings" className="nav-link">Settings</NavLink>}
    </>
  );

  return (
    <>
      <header className="nav">
        <div className="container nav-inner">
          <Link to="/" className="brand" aria-label="VA Plugin Store home">
            <BrandLockup />
          </Link>
          <nav className="nav-links" aria-label="Main">{links}</nav>
          <div className="nav-right">
            <Link to="/cart" className="icon-btn" aria-label={`Cart, ${cart.ids.length} items`}>
              <IconCart />
              {cart.ids.length > 0 && <span className="count-badge">{cart.ids.length}</span>}
            </Link>
            <UserMenu />
            <button className="icon-btn mobile-only" onClick={() => setMobileOpen((o) => !o)} aria-label="Menu">
              <IconMenu />
            </button>
          </div>
        </div>
      </header>
      {mobileOpen && <nav className="mobile-nav" aria-label="Mobile">{links}</nav>}
      {isDesktop && <UpdateBanner />}
      {isDesktop && <LicenseBanner />}
      <main className="page">
        <Outlet />
      </main>
      <footer className="footer">
        <div className="container footer-inner">
          <div className="stack" style={{ gap: 6 }}>
            <span className="mono" style={{ color: 'var(--va-muted)' }}>
              {BRAND.company} · {BRAND.handle}
            </span>
            <span>Plugins and apps for Resolume. Try everything free, buy to remove the watermark.</span>
          </div>
          <div className="row wrap" style={{ gap: 18 }}>
            <Link to="/support"><IconLifebuoy size={14} /> Support</Link>
            <Link to="/support/articles/how-licensing-works">How licensing works</Link>
            <Link to="/redeem">Redeem a code</Link>
            <Link to={`/support/articles/${LEGAL_ARTICLES.terms}`}>Terms of sale</Link>
            <Link to={`/support/articles/${LEGAL_ARTICLES.license}`}>License agreement</Link>
            <Link to={`/support/articles/${LEGAL_ARTICLES.privacy}`}>Privacy</Link>
            <a href={BRAND.instagram} target="_blank" rel="noreferrer">Instagram</a>
          </div>
        </div>
      </footer>
    </>
  );
}
